import { useLazyQuery, useTranslation } from '@gimlite/common';
import {
  ListFlatMultipleCheckType,
  Widget,
  WizardItemType,
  Zone,
} from '@gimlite/ui-v1';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ContractEntity,
  Query,
  QueryContractsArgs,
  QueryGlobalSearchArgs,
} from '../../../../client/graphql';
import { contractsGql } from '../../../../common/gql/contracts.gql';
import { globalSearchGQL } from '../../../../common/gql/globalSearch.gql';
import {
  ContractMotoristLine,
  statusHelper,
} from '../../../../composition/line/contract-motorist.line';
import { ContractWizardInfos } from '../../../../composition/wizard-infos/contract.wizard-infos';
import { ContractsCreateForm } from '../../../../form/contracts-create/contracts-create.form';
import {
  MultipleCheckForm,
  MultipleCheckFormType,
} from '../../../../form/multiple-check/multiple-check.form';
import { AddSeveralMotoristsToAnExistingPoolContractWizardContext } from '../../addSeveralMotoristsToAnExistingPoolContract.wizard';

const pageLimit = 50;

export const ParkersStep = ({
  context: {
    contractBase: contract,
    contractsDetails,
    contractExistedSave,
    contractMode,
    parkingId,
  },
  validate,
  submit,
}: WizardItemType.Config.Component<AddSeveralMotoristsToAnExistingPoolContractWizardContext>) => {
  const { t, lang } = useTranslation();

  const [searchContractsResult, setSearchContractsResult] = useState<
    ContractEntity[] | null
  >(null);

  const [contractsCall, contractsQuery] = useLazyQuery<
    { contracts: Query['contracts'] },
    QueryContractsArgs
  >(contractsGql, {
    variables: {
      page: 1,
      limit: pageLimit,
      order: 'name1',
      parkingId,
      categories: ['PERIOD_SUBSCRIPTION', 'MOTORIST_POOL'],
      isNotInPeriod: [contract.startDate!, contract.endDate!],
    },
  });

  const [globalSearchCall, globalSearchQuery] = useLazyQuery<
    { globalSearch: Query['globalSearch'] },
    QueryGlobalSearchArgs
  >(globalSearchGQL);

  useEffect(() => {
    (async () => {
      try {
        await contractsCall({
          variables: {
            page: 1,
            limit: pageLimit,
            order: 'name1',
            parkingId,
            categories: ['PERIOD_SUBSCRIPTION', 'MOTORIST_POOL'],
            isNotInPeriod: [contract.startDate!, contract.endDate!],
          },
        });
      } catch (error) {
        console.error(error);
      }
    })();
  }, [parkingId, contract.startDate, contract.endDate]);

  const globalSearch = useMemo(() => {
    if (!globalSearchQuery.data) return null;

    return globalSearchQuery.data?.globalSearch;
  }, [globalSearchQuery.data?.globalSearch]);

  const contracts = useMemo(() => {
    if (!contractsQuery.data) return null;

    return contractsQuery.data?.contracts;
  }, [contractsQuery.data?.contracts]);

  const contractsFormattedLine = useCallback(
    (
      contracts: ContractEntity[],
    ): ListFlatMultipleCheckType.Data.Line<any>[] => {
      return contracts.map(
        ({
          _id,
          name1,
          name2,
          ospContractId,
          isEnabled,
          stopDate,
          startDate,
        }) => {
          return {
            _id,
            items: {
              component: (
                <ContractMotoristLine
                  data={{
                    firstName: name1 || null,
                    lastName: name2 || null,
                    contractId: ospContractId!,
                    status: statusHelper({
                      isEnabled,
                      stopDate: new Date(stopDate!),
                      startDate: new Date(startDate),
                    }),
                  }}
                  config={{ action: false }}
                />
              ),
            },
          };
        },
      );
    },
    [],
  );

  const handleSearch = useCallback(
    async (value: string) => {
      if (value && value.length >= 3) {
        const { data } = await globalSearchCall({
          variables: {
            content: value,
            parkingIds: parkingId ? [parkingId] : undefined,
            categories: ['PERIOD_SUBSCRIPTION', 'MOTORIST_POOL'],
            isNotInPeriod: [contract.startDate!, contract.endDate!],
          },
        });

        setSearchContractsResult(data?.globalSearch?.contracts || []);

        submit({
          contractExistedSave: [
            ...contractExistedSave,
            ...(data?.globalSearch?.contracts || []),
          ].reduce((acc: ContractEntity[], { _id, ...rest }) => {
            if (!acc.find((element) => element._id === _id)) {
              return [...acc, { _id, ...rest }];
            }
            return acc;
          }, []),
        });

        try {
          await contractsCall({
            variables: {
              page: 1,
              limit: pageLimit,
              order: 'name1',
              parkingId,
              categories: ['PERIOD_SUBSCRIPTION', 'MOTORIST_POOL'],
              isNotInPeriod: [contract.startDate!, contract.endDate!],
            },
          });
        } catch (error) {
          console.error(error);
        }
      } else {
        setSearchContractsResult(() => null);
      }
    },
    [parkingId, contract.startDate, contract.endDate],
  );

  const { fresh, paging: pagingFormatted } = useMemo((): {
    fresh: ContractEntity[] | undefined;
    paging?: MultipleCheckFormType.Props['data']['paging'];
  } => {
    if (!contracts?.list) {
      return { fresh: undefined };
    } else {
      const listTarget = !searchContractsResult
        ? contracts.list
        : searchContractsResult;

      return {
        fresh: listTarget,
        paging: !searchContractsResult
          ? contracts.paging
          : {
              count: searchContractsResult.length,
              current: 1,
              limit: searchContractsResult.length,
            },
      };
    }
  }, [contracts?.list, contracts?.paging, searchContractsResult]);

  useEffect(() => {
    if (contractMode === 'exist') validate(contractsDetails.checked.length > 0);
  }, [contractsDetails.checked, contractMode]);

  useEffect(() => {
    if (contractMode === 'exist') {
      const newList = contractExistedSave;

      if (contracts?.list) {
        contracts.list.forEach(({ _id, ...rest }) => {
          if (
            contractExistedSave.findIndex((element) => element._id === _id) ===
            -1
          ) {
            newList.push({ _id, ...rest });
          }
        });
      }

      submit({
        contractExistedSave: newList,
      });
    }
  }, [contracts?.list, contractExistedSave, contractMode]);

  useEffect(() => {
    (async () => {
      try {
        await contractsCall({
          variables: {
            page: 1,
            limit: pageLimit,
            order: 'name1',
            parkingId,
            categories: ['PERIOD_SUBSCRIPTION', 'MOTORIST_POOL'],
            isNotInPeriod: [contract.startDate!, contract.endDate!],
          },
        });
      } catch (error) {
        console.error(error);
      }
    })();
  }, [parkingId, contract.startDate, contract.endDate]);

  return (
    <Zone
      config={{
        gap: {
          y: 1,
          x: 1,
        },
        zones: [['info', 'form']],
        rows: ['1fr'],
        columns: ['min-content', '1fr'],
      }}
    >
      <Zone.Area config={{ area: 'info' }}>
        <ContractWizardInfos
          data={{
            APBMode: contract.APBMode,
            APBNext: contract.APBMode !== 'NONE' ? contract.APBNext : null,
            state: contract.state,
            startDate: contract.startDate,
            endDate: contract.endDate,
            description: contract.description,
            reference: contract.reference,
            articleCode: contract.articleCode,
          }}
        />
      </Zone.Area>

      <Zone.Area config={{ area: 'form' }}>
        <Zone
          config={{
            gap: {
              y: 0,
              x: 0,
            },
            zones: [
              // ['link', '.'],
              ['work', 'work'],
            ],
            rows: ['1fr'],
            columns: ['1fr', '1fr'],
          }}
        >
          {/* <Zone.Area config={{ area: 'link' }}>
            <Widget config={{ shadow: false, full: true }}>
              <SwitcherText<
                AddSeveralMotoristsToAnExistingPoolContractWizardContext['contractMode']
              >
                handleEvent={{
                  mode: async (value) => {
                    try {
                      await contractsCall({
                        variables: {
                          page: 1,
                          limit: pageLimit,
                          order: 'name1',
                          parkingId,
                          categories: ['PERIOD_SUBSCRIPTION', 'MOTORIST_POOL'],
                          isNotInPeriod: [
                            contract.startDate!,
                            contract.endDate!,
                          ],
                        },
                      });
                    } catch (error) {
                      console.error(error);
                    }

                    submit({
                      contractMode: value,
                      contractsDetails: {
                        checked: [],
                        created: [],
                      },
                      contractExistedSave:
                        value === 'create' ? [] : contractExistedSave,
                    });
                  },
                }}
                data={{ value: contractMode }}
                config={{
                  mode: [
                    {
                      label: t('chooseExistingContracts?'),
                      value: 'create',
                    },
                    {
                      label: t('createContracts?'),
                      value: 'exist',
                    },
                  ],
                }}
              />
            </Widget>
          </Zone.Area> */}

          <Zone.Area config={{ area: 'work' }}>
            <Widget
              config={{
                title:
                  contractMode === 'exist'
                    ? t('listOfExistingContracts')
                    : t('createSeveralMotoristContracts'),
                shadow: false,
              }}
              state={{
                loading:
                  contractMode === 'exist' ? contractsQuery.loading : undefined,
                refetch:
                  contractMode === 'exist' ? contractsQuery.refetch : undefined,
                error:
                  contractMode === 'exist' ? contractsQuery.error : undefined,
                showPermanentRefetch:
                  contractMode === 'exist' ? false : undefined,
              }}
            >
              {contractMode === 'exist' ? (
                <MultipleCheckForm
                  config={{
                    searchPlaceholder: t('searchAContract'),
                  }}
                  handleEvent={{
                    checkList: (list) => {
                      submit({
                        contractsDetails: {
                          ...contractsDetails,
                          checked: list.map(
                            (id) =>
                              [fresh ? fresh : [], contractExistedSave]
                                .flat()
                                .find(
                                  (element) => element._id === id,
                                ) as ContractEntity,
                          ),
                        },
                      });
                    },
                    paging: async (data) => {
                      try {
                        await contractsCall({
                          variables: {
                            page: data.page,
                            limit: data.limit,
                            order: 'name1',
                            parkingId,
                            categories: [
                              'PERIOD_SUBSCRIPTION',
                              'MOTORIST_POOL',
                            ],
                            isNotInPeriod: [
                              contract.startDate!,
                              contract.endDate!,
                            ],
                          },
                        });
                      } catch (error) {
                        console.error(error);
                      }
                    },
                    search: handleSearch,
                  }}
                  data={{
                    paging: pagingFormatted,
                    checked: contractsDetails.checked.map(
                      (element) => element._id,
                    ),
                    fresh: fresh ? contractsFormattedLine(fresh) : undefined,
                    saved: contractsFormattedLine(contractExistedSave),
                  }}
                />
              ) : (
                <ContractsCreateForm
                  config={{
                    validIfEmpty: true,
                  }}
                  handleEvent={{
                    validate: (isValid) => {
                      validate(isValid && contractsDetails.created.length > 0);
                    },
                    submit: (data) => {
                      submit({
                        contractsDetails: {
                          ...contractsDetails,
                          created: data,
                        },
                      });
                    },
                  }}
                  data={{
                    created: contractsDetails.created,
                  }}
                />
              )}
            </Widget>
          </Zone.Area>
        </Zone>
      </Zone.Area>
    </Zone>
  );
};
